import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSellers, approveSeller, rejectSeller, suspendSeller } from '../../services/api';

function Sellers() {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchSellers();
  }, [filters, pagination.page]);

  const fetchSellers = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      const response = await getSellers(params);
      setSellers(response.data.sellers);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
    } catch (err) {
      setError('Failed to load sellers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSellerAction = async (sellerId, action, reason = '') => {
    let confirmationMessage = `Are you sure you want to ${action} this seller?`;
    
    if (action === 'reject') {
      const userReason = prompt('Please provide a reason for rejection:', 'Application does not meet requirements');
      if (userReason === null) return;
      reason = userReason;
    }

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    try {
      if (action === 'approve') {
        await approveSeller(sellerId);
      } else if (action === 'reject') {
        await rejectSeller(sellerId, reason);
      } else if (action === 'suspend') {
        await suspendSeller(sellerId, reason);
      }
      
      fetchSellers();
    } catch (err) {
      setError(`Failed to ${action} seller. Please try again.`);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (seller) => {
    if (seller.status === 'suspended') {
      return (
        <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-red-900/30 text-red-400 border border-red-700/50">
          <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
          Suspended
        </div>
      );
    }
    if (!seller.is_approved) {
      return (
        <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-900/30 text-yellow-400 border border-yellow-700/50 animate-pulse">
          <div className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></div>
          Pending
        </div>
      );
    }
    return (
      <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-900/30 text-green-400 border border-green-700/50">
        <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
        Active
      </div>
    );
  };

  const getStats = (seller) => {
    return (
      <div className="flex flex-wrap gap-4 text-sm text-gray-400">
        <div className="flex items-center space-x-2">
          <span className="text-lg">📦</span>
          <span>{seller.active_listings || 0} Listings</span>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-lg">💰</span>
          <span>{seller.completed_orders || 0} Sales</span>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-lg">⭐</span>
          <span className="text-yellow-400">{seller.rating_avg || 'N/A'}</span>
        </div>
      </div>
    );
  };

  if (loading && sellers.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#ff660e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300">Loading sellers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 shadow-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center py-6 space-y-4 md:space-y-0">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Seller <span className="text-[#ff660e]">Management</span>
              </h1>
              <p className="text-gray-400 mt-1">Manage seller accounts and approvals</p>
            </div>
            <button
              onClick={() => navigate('/admin/sellers?status=pending')}
              className="bg-gradient-to-r from-[#ff660e] to-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 hover:scale-[1.02] shadow-lg flex items-center space-x-2"
            >
              <span>⚠️</span>
              <span>View Pending Approvals</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 flex items-center justify-between backdrop-blur-sm">
            <div className="flex items-center space-x-3">
              <span className="text-red-400 text-xl">⚠️</span>
              <p className="text-red-300">{error}</p>
            </div>
            <button 
              onClick={() => setError('')}
              className="text-red-400 hover:text-red-300 font-medium transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6 mb-8">
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
            <span className="mr-3 text-[#ff660e] text-xl">🔍</span>
            Filters
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Search</label>
              <input
                type="text"
                placeholder="Search by store name or email..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all"
              >
                <option value="">All Status</option>
                <option value="pending">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            {/* Apply Button */}
            <div className="flex items-end">
              <button
                onClick={fetchSellers}
                className="w-full bg-gradient-to-r from-[#ff660e] to-orange-500 text-white py-3 px-4 rounded-lg font-semibold hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 hover:scale-[1.02] shadow-lg flex items-center justify-center space-x-2"
              >
                <span>🔄</span>
                <span>Apply Filters</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6 hover:border-[#ff660e]/50 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400 uppercase tracking-wide">Total Sellers</p>
                <p className="text-3xl font-bold text-white mt-2">{pagination.total}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-[#ff660e] to-orange-400 rounded-xl flex items-center justify-center">
                <span className="text-2xl text-white">🏪</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6 hover:border-[#ff660e]/50 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400 uppercase tracking-wide">Pending</p>
                <p className="text-3xl font-bold text-white mt-2">
                  {sellers.filter(s => !s.is_approved && s.status === 'active').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-[#ff660e] to-orange-500 rounded-xl flex items-center justify-center">
                <span className="text-2xl text-white">⏳</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6 hover:border-[#ff660e]/50 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400 uppercase tracking-wide">Active</p>
                <p className="text-3xl font-bold text-white mt-2">
                  {sellers.filter(s => s.is_approved && s.status === 'active').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-[#ff660e] to-orange-600 rounded-xl flex items-center justify-center">
                <span className="text-2xl text-white">✅</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6 hover:border-[#ff660e]/50 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-400 uppercase tracking-wide">Suspended</p>
                <p className="text-3xl font-bold text-white mt-2">
                  {sellers.filter(s => s.status === 'suspended').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-[#ff660e] to-orange-700 rounded-xl flex items-center justify-center">
                <span className="text-2xl text-white">⏸️</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sellers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sellers.map((seller) => (
            <div key={seller.seller_id} className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden hover:shadow-xl hover:border-[#ff660e]/50 transition-all duration-300 group">
              {/* Store Header */}
              <div className="p-6 border-b border-gray-700">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white group-hover:text-[#ff660e] transition-colors mb-1">
                      {seller.store_name}
                    </h3>
                    {seller.store_location && (
                      <p className="text-sm text-gray-400 mb-2 flex items-center">
                        <span className="mr-1">📍</span>
                        {seller.store_location}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      {getStatusBadge(seller)}
                      <span className="text-xs text-gray-500">
                        {new Date(seller.user_joined).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  {seller.store_logo_url ? (
                    <img
                      src={seller.store_logo_url}
                      alt={seller.store_name}
                      className="w-14 h-14 rounded-xl object-cover ring-2 ring-gray-700"
                    />
                  ) : (
                    <div className="w-14 h-14 bg-gradient-to-br from-[#ff660e] to-orange-400 rounded-xl flex items-center justify-center text-white font-bold text-xl ring-2 ring-gray-700">
                      {seller.store_name?.[0]}
                    </div>
                  )}
                </div>
              </div>

              {/* Seller Info */}
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-400 mb-1">Seller</p>
                    <p className="text-sm font-semibold text-white">
                      {seller.first_name} {seller.last_name}
                    </p>
                    <p className="text-sm text-gray-400">{seller.email}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-400 mb-2">Store Stats</p>
                    {getStats(seller)}
                  </div>

                  {seller.store_description && (
                    <div>
                      <p className="text-sm font-medium text-gray-400 mb-1">Description</p>
                      <p className="text-sm text-gray-400 line-clamp-2">{seller.store_description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 py-4 bg-gray-750 border-t border-gray-700">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => navigate(`/admin/sellers/${seller.seller_id}`)}
                    className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors flex items-center space-x-2 hover:scale-105 transition-transform"
                  >
                    <span>👁️</span>
                    <span>View Details</span>
                  </button>

                  <div className="flex space-x-2">
                    {!seller.is_approved && seller.status === 'active' && (
                      <>
                        <button
                          onClick={() => handleSellerAction(seller.user_id, 'approve')}
                          className="bg-gradient-to-r from-green-600 to-emerald-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-green-700 hover:to-emerald-600 transition-all duration-200 hover:scale-105 shadow flex items-center space-x-1"
                        >
                          <span>✅</span>
                          <span>Approve</span>
                        </button>
                        <button
                          onClick={() => handleSellerAction(seller.user_id, 'reject')}
                          className="bg-gradient-to-r from-red-600 to-red-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-700 hover:to-red-600 transition-all duration-200 hover:scale-105 shadow flex items-center space-x-1"
                        >
                          <span>❌</span>
                          <span>Reject</span>
                        </button>
                      </>
                    )}

                    {seller.is_approved && seller.status === 'active' && (
                      <button
                        onClick={() => handleSellerAction(seller.user_id, 'suspend')}
                        className="bg-gradient-to-r from-red-600 to-red-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-700 hover:to-red-600 transition-all duration-200 hover:scale-105 shadow flex items-center space-x-1"
                      >
                        <span>⏸️</span>
                        <span>Suspend</span>
                      </button>
                    )}

                    {seller.status === 'suspended' && (
                      <button
                        onClick={() => handleSellerAction(seller.user_id, 'approve')}
                        className="bg-gradient-to-r from-green-600 to-emerald-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-green-700 hover:to-emerald-600 transition-all duration-200 hover:scale-105 shadow flex items-center space-x-1"
                      >
                        <span>✅</span>
                        <span>Activate</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {sellers.length === 0 && !loading && (
          <div className="text-center py-16 border-t border-gray-700 mt-8">
            <div className="text-gray-400 text-6xl mb-4">🏪</div>
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No sellers found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your search or filters</p>
            <button
              onClick={() => {
                setFilters({ search: '', status: '' });
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="bg-gradient-to-r from-[#ff660e] to-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 hover:scale-[1.02] shadow-lg"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="mt-8 bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="text-sm text-gray-400">
                Showing <span className="font-semibold text-white">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-semibold text-white">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-semibold text-white">{pagination.total}</span> sellers
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-600 hover:border-[#ff660e] hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-700 disabled:hover:border-gray-600 flex items-center space-x-2"
                >
                  <span>←</span>
                  <span>Previous</span>
                </button>
                <div className="px-4 py-2 text-sm text-gray-400">
                  Page <span className="font-semibold text-white">{pagination.page}</span> of{' '}
                  <span className="font-semibold text-white">{pagination.pages}</span>
                </div>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-600 hover:border-[#ff660e] hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-700 disabled:hover:border-gray-600 flex items-center space-x-2"
                >
                  <span>Next</span>
                  <span>→</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Sellers;